import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Language } from '@app/models';
import { AuthService, DictionaryService, ProfileService, SettingsService, ToastService } from '@app/services';
import { MessageType, StoreKey, UUID } from '@app/shared';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CacheMap } from 'app/core/classes';

@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  languages!: CacheMap<Language>;
  selectedLang: Language | undefined;
  showPassword: boolean = false;
  loading: boolean = false;
  initializing: boolean = true;
  idToken: string;

  constructor(
    public translate: TranslateService,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    public settingsService: SettingsService,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    private dictionary: DictionaryService,
  ) {
    const frag = route.snapshot.fragment.split('id_token=')[1];
    this.idToken = this.route.snapshot.queryParams?.id_token || frag;
  }

  ngOnDestroy(): void {}

  ngOnInit() {
    this.doLogin();
  }

  getRedirectUrl(): string | null {
    const redirectUrl = this.route.snapshot.queryParams['redirect_url'];
    if (!redirectUrl) {
      return null;
    } else {
      return redirectUrl;
    }
  }

  async doLogin() {
    try {
      const deviceId = UUID.randomV4();
      const userData = await this.authService.login(deviceId, this.idToken);
      if (!userData) throw new Error('NOT_FOUND');
      localStorage.setItem(StoreKey.DEVICEID, deviceId);
      if (userData.token) sessionStorage.setItem(StoreKey.TOKEN, userData.token);
      if (userData.refreshToken) localStorage.setItem(StoreKey.REFRESHTOKEN, userData.refreshToken);

      localStorage.setItem(StoreKey.CUSTOMERID, userData.customerId);
      if (userData?.forceResetPassword) {
        return this.router.navigate(['/auth/change-password']);
      }

      const profile = await this.profileService.read();
      const languages = await this.dictionary.getLanguages();
      const userLanguage = languages.get(profile.settings.languageId);
      this.settingsService.loadLanguage(userLanguage);
      this.authService.loggedStatus$.next(true);

      this.toastService.showToast(MessageType.SUCCESS, this.translate.instant('TOAST.LOGIN_SUCCESS'));
      const redirectUrl = this.getRedirectUrl();
      if (!redirectUrl) {
        this.router.navigate(['/licenses']);
      } else {
        if (redirectUrl.includes('?')) {
          const [baseUrl, params] = redirectUrl.split('?');

          let queryParams: any = params
            ?.split('&')
            ?.map((item) => item.split('='))
            .reduce((acc, val) => {
              return { ...acc, [val[0]]: val[1] };
            }, {});
          if (Object.keys(queryParams)?.length > 0) {
            this.router.navigate([baseUrl], { queryParams: queryParams });
          } else {
            this.router.navigate([baseUrl]);
          }
        } else {
          this.router.navigate([redirectUrl]);
        }
      }
    } catch (error: any) {
      console.error(error);
      this.toastService.showErrorToast(error);
      this.router.navigate(['']);
    } finally {
      this.initializing = false;
    }
  }
}
